import { supabase } from './supabase'
import { reportSilent } from './report'

/**
 * Ask the moderator Worker to read a song's lyrics.
 *
 * The Worker (moderator/src/index.ts) verifies the access token sent here
 * against Supabase's published signing key, then reads the lyrics from the
 * database itself — deliberately not from us. Sending the text from the browser
 * would let a modified client submit something clean, pass, and store something
 * else; the check would be of a document nobody ever reads.
 *
 * Fire and forget. The verdict is written server-side into content_flags, so
 * there is nothing here for the caller to act on, and a moderator that is down
 * must never be able to stop a writer working. Every failure path returns
 * quietly.
 */
const URL = import.meta.env.VITE_MODERATOR_URL

export async function moderateLyrics(songId) {
  if (!URL || !songId) return

  try {
    const { data } = await supabase.auth.getSession()
    const token = data?.session?.access_token
    if (!token) return

    const res = await fetch(URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ song_id: songId }),
      // The writer is already on the next screen. Nothing waits on this, and it
      // must not hold the page open on a slow model call.
      keepalive: true,
    })

    // Silent to the writer, loud to the console.
    //
    // The first version swallowed everything, which is right for the person
    // using the app and useless for anyone fixing it: the first real run
    // produced no flag and no trace, and there was no way to tell a rate limit
    // from a bad token from a call that never happened at all.
    if (!res.ok) {
      const body = await res.text().catch(() => '')
      console.debug('[moderate] rejected', res.status, body)
      reportSilent(new Error(`moderate rejected ${res.status}`), 'moderation', {
        status: res.status,
      })
    } else {
      console.debug('[moderate]', await res.json().catch(() => ({})))
    }
  } catch (e) {
    // Offline, blocked by an extension, Worker down. None of it is the writer's
    // problem and none of it changes what they can do next.
    console.debug('[moderate] failed', e?.message)
    reportSilent(e, 'moderation')
  }
}
